import {ToastContent} from "components";
import {useAppDispatch} from "hooks/index";
import {useCallback, useEffect, useRef} from "react";
import {useLocation} from "react-router-dom";
import {toast} from "react-toastify";
import {logoutUser} from "store/user/userAsyncAction";
import {clearCaches} from "utils/lazyWithRetry";

const useVersionChangeHook = () => {
	const dispatch = useAppDispatch();
	const location = useLocation();
	const lastCheckedBuildTime = useRef<string | null>(null);
	const isCheckingVersion = useRef(false);

	const checkBuildTimeChange = useCallback(
		async (serverBuildTime: string, bumpType = "patch") => {
			const currentBuildTime = localStorage.getItem("appBuildTime");

			if (process.env?.REACT_APP_ENV === "development") {
				console.info("📦 Server build time:", serverBuildTime);
				console.info("💾 Stored build time:", currentBuildTime);
				console.info("🔖 Bump type:", bumpType);
			}

			if (currentBuildTime && currentBuildTime !== serverBuildTime) {
				if (process.env?.REACT_APP_ENV === "development") {
					console.log("🎉 New deploy detected:", serverBuildTime, `(${bumpType})`);
				}

				localStorage.setItem("appBuildTime", serverBuildTime);

				if (bumpType === "major") {
					// Major bump: full cleanup + forced logout (breaking changes expected)
					sessionStorage.clear();

					if (window.indexedDB) {
						try {
							const databases = await window.indexedDB.databases();
							databases.forEach((db) => {
								window.indexedDB.deleteDatabase(db.name as string);
							});
						} catch (e) {
							console.warn("Failed to clear IndexedDB:", e);
						}
					}

					await clearCaches();

					await dispatch(logoutUser());
					window.location.reload();
				} else {
					// Patch / minor: clear module caches and reload — user session preserved
					await clearCaches();
					toast.info(ToastContent, {
						autoClose: 3000,
						data: {title: "A new version is available. Refreshing..."},
					});
					setTimeout(() => window.location.reload(), 3000);
				}
			} else if (!currentBuildTime) {
				if (process.env?.REACT_APP_ENV === "development") {
					console.info("📝 First load, storing build time:", serverBuildTime);
				}
				localStorage.setItem("appBuildTime", serverBuildTime);
			} else {
				if (process.env?.REACT_APP_ENV === "development") {
					console.info("✅ App is up to date");
				}
			}
		},
		[dispatch],
	);

	const checkForUpdate = useCallback(
		async (force = false) => {
			// Prevent concurrent checks
			if (isCheckingVersion.current) {
				return;
			}

			isCheckingVersion.current = true;

			try {
				// Fetch version.json from public folder (served as static file)
				// This works because files in /public are served at root /
				const response = await fetch("/version.json?" + Date.now(), {
					cache: "no-store",
					headers: {
						"Cache-Control": "no-cache",
					},
				});

				// Check if it's actually JSON
				const contentType = response.headers.get("content-type");
				if (!contentType || !contentType.includes("application/json")) {
					console.warn("version.json not found or wrong content type");

					return;
				}

				const data = await response.json();

				if (!data || !data.buildTime) {
					console.warn("Invalid version data");

					return;
				}

				// Only proceed if buildTime is different from last checked
				if (!force && lastCheckedBuildTime.current === data.buildTime) {
					return;
				}

				lastCheckedBuildTime.current = data.buildTime;
				await checkBuildTimeChange(data.buildTime, data.bumpType ?? "patch");
			} catch (error) {
				console.error("Error checking version:", error);
			} finally {
				isCheckingVersion.current = false;
			}
		},
		[checkBuildTimeChange],
	);

	// Check on mount
	useEffect(() => {
		const initialCheck = setTimeout(() => {
			checkForUpdate(true);
		}, 500);

		return () => {
			clearTimeout(initialCheck);
		};
	}, [checkForUpdate]);

	// Poll every 30 minutes for users who stay on the same page without switching tabs.
	// Visibility change and route change cover the common cases; this catches the rest.
	useEffect(() => {
		const intervalId = setInterval(() => {
			checkForUpdate();
		}, 30 * 60 * 1000);

		return () => clearInterval(intervalId);
	}, [checkForUpdate]);

	// Check when user returns to the tab (visibility change)
	// This is more efficient than polling and catches deployments when user comes back
	useEffect(() => {
		const handleVisibilityChange = () => {
			if (document.visibilityState === "visible") {
				// User returned to the tab - check for updates
				checkForUpdate();
			}
		};

		document.addEventListener("visibilitychange", handleVisibilityChange);

		return () => {
			document.removeEventListener("visibilitychange", handleVisibilityChange);
		};
	}, [checkForUpdate]);

	// Check on route change - this is the most important check
	// since it happens right before chunks would be loaded
	useEffect(() => {
		// Small delay to avoid blocking navigation
		const timeoutId = setTimeout(() => {
			checkForUpdate();
		}, 100);

		return () => {
			clearTimeout(timeoutId);
		};
	}, [location.pathname, checkForUpdate]);
};

export default useVersionChangeHook;
